import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCaja } from '../contexts/CajaContext';
import { getApiUrl } from '../config/apiConfig';
import AperturaCajaModal from './AperturaCajaModal';
import CierreCajaModal from './CierreCajaModal';
import AsignacionCajerosModal from './AsignacionCajerosModal';
import './Caja.css';
import './Mesas.css'; // FAB styles

const CajaComponent = () => {
  const navigate = useNavigate();
  const { auth } = useAuth();
  const { caja, cajasAbiertas, cajaActiva, seleccionarCaja, recargarEstadoCaja } = useCaja();

  // Estados del componente
  const [pedidos, setPedidos] = useState([]);
  const [mesas, setMesas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mensaje, setMensaje] = useState('');

  // Piso asignado al CAJERO en tiempo real (evita JWT desactualizado)
  const [pisoAsignadoDB, setPisoAsignadoDB] = useState(auth?.user?.piso_asignado ?? null);

  // Estados para los filtros
  const [filtroPiso, setFiltroPiso] = useState(null);
  const [filtroTipo, setFiltroTipo] = useState('todos');
  const [filtroMesa, setFiltroMesa] = useState('todos');
  const [filtroEstado, setFiltroEstado] = useState('todos');
  const [busqueda, setBusqueda] = useState('');

  // Tab activa: 'pedidos' | 'mesas' | 'cuentas'
  const [vistaActiva, setVistaActiva] = useState('pedidos');

  // Estado del tab Cuentas
  const [cuentasData, setCuentasData] = useState(null);
  const [cuentasLoading, setCuentasLoading] = useState(false);
  const [cuentasError, setCuentasError] = useState(null);

  // Estados para controlar la visibilidad de los modales
  const [mostrarModalApertura, setMostrarModalApertura] = useState(false);
  const [mostrarModalCierre, setMostrarModalCierre] = useState(false);
  const [mostrarModalAsignacion, setMostrarModalAsignacion] = useState(false);
  const [cajaACerrar, setCajaACerrar] = useState(null); // caja específica que se va a cerrar

  // --- LÓGICA DE OBTENCIÓN DE DATOS ---
  const fetchPedidos = useCallback(async (piso, isSilent = false) => {
    if (!caja.estaAbierta || !caja.fechaApertura || piso === null) {
      setPedidos([]);
      if (!isSilent) setLoading(false);
      return;
    }

    if (!isSilent) setLoading(true);
    try {
      const fechaAperturaCodificada = encodeURIComponent(caja.fechaApertura);
      // CAJERO: filtrar por su caja (+ pedidos sin caja aún para poder cobrarlos)
      const esCajero = auth?.user?.rol === 'CAJERO';
      const filtroCaja = esCajero && cajaActiva?.id ? `&id_caja=${cajaActiva.id}` : '';
      const apiUrl = getApiUrl(`/pedidos?fecha_inicio=${fechaAperturaCodificada}&piso=${piso}${filtroCaja}`);

      const response = await fetch(apiUrl, { headers: { 'Authorization': `Bearer ${auth?.token}` } });
      if (!response.ok) {
        const errorText = await response.text();
        console.error("Error del servidor al obtener pedidos:", errorText);
        throw new Error('Error al cargar los pedidos del día');
      }
      const data = await response.json();
      setPedidos(data);
    } catch (err) {
      setError(err.message);
    } finally {
      if (!isSilent) setLoading(false);
    }
  }, [auth?.token, auth?.user?.rol, caja.estaAbierta, caja.fechaApertura, cajaActiva?.id]);

  // Cargar datos de Cuentas (resumen por método de pago)
  const fetchCuentas = useCallback(async () => {
    if (!caja.estaAbierta || !cajaActiva?.id) return;
    setCuentasLoading(true);
    setCuentasError(null);
    try {
      const response = await fetch(getApiUrl(`/caja/resumen?id_caja=${cajaActiva.id}`), {
        headers: { 'Authorization': `Bearer ${auth?.token}` }
      });
      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.message || 'Error al cargar cuentas');
      }
      const data = await response.json();
      setCuentasData(data);
    } catch (err) {
      setCuentasError(err.message);
    } finally {
      setCuentasLoading(false);
    }
  }, [auth?.token, caja.estaAbierta, cajaActiva?.id]);

  // --- EFECTOS DE REACT ---

  useEffect(() => {
    const fetchInitialData = async () => {
      if (!caja.estaAbierta) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        // PASO 1: Para CAJEROs, obtener el piso asignado en tiempo real ANTES de fetchear pedidos.
        // Esto elimina la race condition donde pisoAsignadoDB llegaba tarde y se cargaban pedidos del piso incorrecto.
        let pisoRealCajero = null;
        if (auth?.user?.rol === 'CAJERO' && auth?.token) {
          try {
            const asigResp = await fetch(getApiUrl('/usuarios-admin/mis-asignaciones'), {
              headers: { 'Authorization': `Bearer ${auth.token}` }
            });
            if (asigResp.ok) {
              const asigData = await asigResp.json();
              if (asigData?.piso_asignado != null) {
                pisoRealCajero = Number(asigData.piso_asignado);
                setPisoAsignadoDB(pisoRealCajero);
              }
            }
          } catch (_) { /* silencioso, usar fallback */ }
        }

        // PASO 2: Cargar mesas
        const responseMesas = await fetch(getApiUrl('/mesas'), { headers: { 'Authorization': `Bearer ${auth?.token}` } });
        if (!responseMesas.ok) throw new Error('No se pudieron cargar las mesas.');

        const dataMesas = await responseMesas.json();
        setMesas(dataMesas);

        const pisosValidos = [...new Set(dataMesas.map(m => Number(m.piso)).filter(p => p !== 0))].sort((a, b) => a - b);
        let pisoInicial = (pisosValidos.length > 0 ? pisosValidos[0] : 1);

        // PASO 3: Determinar piso a cargar — el piso del CAJERO siempre tiene prioridad
        const pisoAFetchear = pisoRealCajero !== null ? pisoRealCajero : pisoInicial;

        setFiltroPiso(pisoAFetchear);
        await fetchPedidos(pisoAFetchear, false);

      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    if (!caja.loading) {
      fetchInitialData();
    }
  }, [caja.loading, caja.estaAbierta, auth?.token]);

  // Efecto para buscar pedidos cuando el usuario cambia de piso manualmente
  useEffect(() => {
    if (filtroPiso !== null && !loading) {
      fetchPedidos(filtroPiso, false);
    }
  }, [filtroPiso]);

  // Sincronizar filtroPiso si pisoAsignadoDB cambia externamente (ej. admin reasigna piso)
  useEffect(() => {
    if (auth?.user?.rol === 'CAJERO' && pisoAsignadoDB !== null) {
      setFiltroPiso(Number(pisoAsignadoDB));
    }
  }, [pisoAsignadoDB, auth?.user?.rol]);

  // Efecto para el refresco periódico de datos
  useEffect(() => {
    if (!caja.estaAbierta || filtroPiso === null) return;
    const intervalId = setInterval(() => fetchPedidos(filtroPiso, true), 15000);
    return () => clearInterval(intervalId);
  }, [caja.estaAbierta, filtroPiso, fetchPedidos]);

  // Cargar cuentas cuando se activa el tab o cambia la caja activa
  useEffect(() => {
    if (vistaActiva === 'cuentas') {
      fetchCuentas();
    }
  }, [vistaActiva, cajaActiva?.id]);

  // --- MANEJADORES DE EVENTOS ---

  const handleAperturaExitosa = (exitoso = false) => {
    setMostrarModalApertura(false);
    if (exitoso === true) {
      setMensaje('¡Caja aperturada con éxito!');
      setTimeout(() => setMensaje(''), 3000);
      recargarEstadoCaja();
    }
  };

  const handleCierreExitoso = (exitoso) => {
    setMostrarModalCierre(false);
    setCajaACerrar(null);
    if (exitoso) {
      setMensaje('¡Caja cerrada con éxito!');
      setTimeout(() => setMensaje(''), 3000);
      recargarEstadoCaja();
      // Si el tab de cuentas está activo, refrescar los datos de la caja que quedó activa
      if (vistaActiva === 'cuentas') {
        fetchCuentas();
      }
    }
  };

  const pisosDisponibles = useMemo(() => {
    return [...new Set(
      mesas.filter(m => m.id !== 910100 && Number(m.piso) !== 0).map(m => Number(m.piso))
    )].sort((a, b) => a - b);
  }, [mesas]);

  const mesasDelPiso = useMemo(() => {
    return mesas
      .filter(m => Number(m.piso) === Number(filtroPiso) && m.id !== 910100)
      .sort((a, b) => a.numero - b.numero);
  }, [mesas, filtroPiso]);

  const pedidosFiltrados = useMemo(() => {
    return pedidos.filter(pedido => {
      if (filtroTipo === 'mesas' && pedido.id_mesa === 910100) return false;
      if (filtroTipo === 'directos' && pedido.id_mesa !== 910100) return false;
      if (filtroMesa !== 'todos' && pedido.id_mesa !== filtroMesa) return false;
      if (filtroEstado !== 'todos' && pedido.estado !== filtroEstado) return false;
      if (busqueda.trim() !== '' && !pedido.id.toString().includes(busqueda.trim())) return false;
      return true;
    });
  }, [pedidos, filtroTipo, filtroMesa, filtroEstado, busqueda]);

  const handleFiltroTipoClick = (tipo) => { setFiltroTipo(tipo); setFiltroMesa('todos'); };
  const handleFiltroMesaClick = (idMesa) => { setFiltroMesa(idMesa); setFiltroTipo('todos'); };
  const formatearTiempo = (timestamp) => new Date(timestamp).toLocaleTimeString();
  const getEstadoClass = (estado) => {
    switch (estado) {
      case 'pendiente_pagado': case 'pendiente': return 'estado-pendiente';
      case 'en_prep_pagado': case 'en_prep': return 'estado-preparacion';
      case 'listo_pagado': case 'listo': return 'estado-listo';
      case 'entregado_pagado': case 'entregado': return 'estado-entregado';
      case 'pagado': return 'estado-pagado';
      case 'cancelado': return 'estado-cancelado';
      default: return '';
    }
  };
  const verDetallePedido = (pedidoId) => navigate(`/pedidos/${pedidoId}`, { state: { from: 'caja' } });

  const getMesaEstadoClass = (estado) => {
    switch (estado) {
      case 'ocupada': return 'mesa-card-caja ocupada';
      case 'reservada': return 'mesa-card-caja reservada';
      default: return 'mesa-card-caja libre';
    }
  };

  // --- RENDERIZADO CONDICIONAL ---

  if (caja.loading) {
    return <div className="loading">Verificando estado de la caja...</div>;
  }

  // CAJERO sin caja asignada → mostrar pantalla de apertura aunque haya cajas de otros cajeros abiertas
  if (auth.user.rol === 'CAJERO' && !cajaActiva) {
    return (
      <div className="caja-cerrada-container">
        <h2>Sin Caja Asignada</h2>
        <p>No tienes una caja asignada. Abre tu propia caja para comenzar tu turno.</p>
        <button className="btn-abrir-caja" onClick={() => setMostrarModalApertura(true)}>
          Abrir Mi Caja
        </button>
        {mostrarModalApertura && <AperturaCajaModal onClose={handleAperturaExitosa} />}
      </div>
    );
  }

  if (!caja.estaAbierta) {
    return (
      <div className="caja-cerrada-container">
        <h2>Caja Cerrada</h2>
        <p>Para comenzar a registrar y ver pedidos, primero debes aperturar la caja.</p>
        <button className="btn-abrir-caja" onClick={() => setMostrarModalApertura(true)}>Aperturar Caja</button>
        {mostrarModalApertura && <AperturaCajaModal onClose={handleAperturaExitosa} />}
      </div>
    );
  }

  return (
    <div className="caja-container">
      {mensaje && <div className="mensaje-exito">{mensaje}</div>}

      <div className="caja-header">
        <h2>Módulo de Caja</h2>
        <div className="acciones-caja">
          {/* ENCARGADO: selector multi-caja + botones de gestión */}
          {auth.user.rol === 'ENCARGADO' && (
            <>
              {cajasAbiertas.length > 1 ? (
                <div className="selector-caja-activa">
                  <label>Caja activa:</label>
                  <select
                    value={cajaActiva?.id || ''}
                    onChange={(e) => {
                      const cajaSel = cajasAbiertas.find(c => c.id === parseInt(e.target.value));
                      if (cajaSel) seleccionarCaja(cajaSel);
                    }}
                  >
                    {cajasAbiertas.map(c => (
                      <option key={c.id} value={c.id}>{c.nombre}</option>
                    ))}
                  </select>
                </div>
              ) : (
                cajaActiva && <span className="nombre-caja-activa">📦 {cajaActiva.nombre}</span>
              )}
              <button className="btn-nueva-caja" onClick={() => setMostrarModalApertura(true)}>
                + Nueva Caja
              </button>
              <button className="btn-asignar-cajeros" onClick={() => setMostrarModalAsignacion(true)}>
                Asignar Cajeros
              </button>
            </>
          )}

          {/* CAJERO: solo muestra el nombre de su caja asignada */}
          {auth.user.rol !== 'ENCARGADO' && cajaActiva && (
            <span className="nombre-caja-activa">📦 {cajaActiva.nombre}</span>
          )}

          {/* ENCARGADO con múltiples cajas: un botón de cierre por cada caja */}
          {auth.user.rol === 'ENCARGADO' && cajasAbiertas.length > 1 ? (
            cajasAbiertas.map(c => (
              <button
                key={c.id}
                className="btn-cerrar-caja"
                onClick={() => { setCajaACerrar(c); setMostrarModalCierre(true); }}
              >
                Cerrar {c.nombre || `Caja #${c.id}`}
              </button>
            ))
          ) : (
            // CAJERO o ENCARGADO con 1 sola caja: botón único con nombre
            <button
              className="btn-cerrar-caja"
              onClick={() => { setCajaACerrar(cajaActiva); setMostrarModalCierre(true); }}
            >
              Cerrar {cajaActiva?.nombre || 'Caja'}
            </button>
          )}
        </div>
      </div>

      {/* Barra de selección de piso — oculta si CAJERO tiene piso asignado */}
      {pisosDisponibles.length > 0 && !(auth?.user?.rol === 'CAJERO' && pisoAsignadoDB !== null) && (
        <div className="caja-barra-piso">
          <span className="caja-piso-label">Piso:</span>
          <div className="grupo-botones-piso">
            {pisosDisponibles.map(piso => (
              <button key={piso} className={`btn-filtro-piso ${filtroPiso === piso ? 'active' : ''}`} onClick={() => setFiltroPiso(piso)}>
                Piso {piso}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Tabs de vista */}
      <div className="caja-tabs">
        <button
          className={`caja-tab ${vistaActiva === 'pedidos' ? 'active' : ''}`}
          onClick={() => setVistaActiva('pedidos')}
        >
          Pedidos del Turno
        </button>
        <button
          className={`caja-tab ${vistaActiva === 'mesas' ? 'active' : ''}`}
          onClick={() => setVistaActiva('mesas')}
        >
          Estado de Mesas
        </button>
        <button
          className={`caja-tab ${vistaActiva === 'cuentas' ? 'active' : ''}`}
          onClick={() => setVistaActiva('cuentas')}
        >
          Cuentas
        </button>
      </div>

      {/* ===== VISTA: PEDIDOS ===== */}
      {vistaActiva === 'pedidos' && (
        <>
          <div className="panel-filtros">
            <div className="grupo-botones-tipo">
              <button className={`btn-filtro-tipo ${filtroTipo === 'todos' && filtroMesa === 'todos' ? 'active' : ''}`} onClick={() => handleFiltroTipoClick('todos')}>Todos</button>
              <button className={`btn-filtro-tipo ${filtroTipo === 'mesas' ? 'active' : ''}`} onClick={() => handleFiltroTipoClick('mesas')}>Pedidos de Mesa</button>
              <button className={`btn-filtro-tipo ${filtroTipo === 'directos' ? 'active' : ''}`} onClick={() => handleFiltroTipoClick('directos')}>Pedidos Directos</button>
            </div>
            {filtroTipo !== 'directos' && (
              <div className="grupo-botones-mesas">
                <span className="grupo-label">Mesas del Piso {filtroPiso}:</span>
                {mesasDelPiso.map(mesa => (
                  <button key={mesa.id} className={`btn-filtro-mesa ${filtroMesa === mesa.id ? 'active' : ''}`} onClick={() => handleFiltroMesaClick(mesa.id)}>
                    {mesa.numero}
                  </button>
                ))}
              </div>
            )}
            <div className="filtros-secundarios">
              <div className="filtro-item">
                <label>Filtrar por estado:</label>
                <select value={filtroEstado} onChange={(e) => setFiltroEstado(e.target.value)}>
                  <option value="todos">Todos los estados</option>
                  <option value="pendiente">Pendiente</option>
                  <option value="en_prep">En preparación</option>
                  <option value="listo">Listo</option>
                  <option value="entregado">Entregado</option>
                  <option value="pagado">Pagado</option>
                  <option value="cancelado">Cancelado</option>
                </select>
              </div>
              <div className="filtro-item">
                <label>Buscar por N° de Pedido:</label>
                <input type="text" placeholder="Ej: 123" value={busqueda} onChange={(e) => setBusqueda(e.target.value)} />
              </div>
            </div>
          </div>

          {loading ? (
            <div className="loading">Cargando pedidos...</div>
          ) : (
            <div className="lista-pedidos-caja">
              {pedidosFiltrados.length === 0 ? (
                <div className="sin-pedidos">No hay pedidos para mostrar con los filtros actuales.</div>
              ) : (
                pedidosFiltrados.map(pedido => {
                  // Ahora evaluamos si EL PEDIDO tiene el estado 'listo', o si el backend nos indica
                  // explícitamente mediante una bandera que hay productos individuales listos dentro de él.
                  const tieneProductosListos = pedido.tiene_productos_listos === true || ['listo', 'listo_pagado'].includes(pedido.estado);
                  return (
                    <div key={pedido.id} className={`pedido-card-caja ${getEstadoClass(pedido.estado)} ${tieneProductosListos ? 'parpadeo' : ''}`} onClick={() => verDetallePedido(pedido.id)}>
                      <div className="pedido-card-header">
                        <span className="pedido-id">Pedido #{pedido.id}</span>
                        {pedido.id_mesa === 910100 ? <span className="tipo-pedido directo">Directo</span> : <span className="tipo-pedido mesa">Mesa {pedido.numero_mesa}</span>}
                      </div>
                      <div className="pedido-card-body">
                        <p><strong>Estado:</strong> {pedido.estado.replace(/_/g, ' ')}</p>
                        <p><strong>Hora:</strong> {formatearTiempo(pedido.tiempo_inicio)}</p>
                        <p><strong>Total:</strong> S/. {Number(pedido.total).toFixed(2)}</p>
                      </div>
                      {tieneProductosListos && (
                        <div className="indicador-productos-listos" style={{ marginTop: '10px' }}>
                          ¡Productos listos!
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          )}
        </>
      )}

      {/* ===== VISTA: MESAS ===== */}
      {vistaActiva === 'mesas' && (
        <div className="vista-mesas-caja">
          {loading ? (
            <div className="loading">Cargando mesas...</div>
          ) : (
            <>
              <div className="leyenda-mesas-caja">
                <span className="leyenda-item"><span className="dot libre"></span>Libre</span>
                <span className="leyenda-item"><span className="dot ocupada"></span>Ocupada</span>
                <span className="leyenda-item"><span className="dot reservada"></span>Reservada</span>
              </div>
              <div className="grid-mesas-caja">
                {mesasDelPiso.length === 0 ? (
                  <div className="sin-pedidos">No hay mesas registradas para este piso.</div>
                ) : (
                  mesasDelPiso.map(mesa => (
                    <div
                      key={mesa.id}
                      className={getMesaEstadoClass(mesa.estado)}
                      onClick={() => mesa.estado === 'ocupada' && navigate(`/mesa/${mesa.id}/pedidos`)}
                      title={mesa.estado === 'ocupada' ? 'Ver pedidos de esta mesa' : ''}
                    >
                      <div className="mesa-numero-caja">Mesa {mesa.numero}</div>
                      <div className="mesa-estado-caja">{mesa.estado.charAt(0).toUpperCase() + mesa.estado.slice(1)}</div>
                      {mesa.mesero_nombre && (
                        <div className="mesa-mesero-caja">{mesa.mesero_nombre}</div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* ===== VISTA: CUENTAS ===== */}
      {vistaActiva === 'cuentas' && (
        <div className="vista-cuentas-caja">
          <div className="cuentas-header">
            <h3>Ingresos del Turno Actual</h3>
            {cajaActiva && (
              <p className="cuentas-caja-nombre">
                Caja: <strong>{cajaActiva.nombre}</strong>
                {cajaActiva.fecha_apertura && (
                  <span className="cuentas-fecha-apertura">
                    &nbsp;— Apertura: {new Date(cajaActiva.fecha_apertura).toLocaleString()}
                  </span>
                )}
              </p>
            )}
            <button
              className="btn-refrescar-cuentas"
              onClick={fetchCuentas}
              disabled={cuentasLoading}
            >
              {cuentasLoading ? 'Actualizando...' : '↻ Actualizar'}
            </button>
          </div>

          {cuentasLoading && <div className="loading">Cargando ingresos...</div>}
          {cuentasError && <div className="error-message">{cuentasError}</div>}

          {!cuentasLoading && !cuentasError && cuentasData && (
            <div className="cuentas-contenido">
              <table className="cuentas-tabla">
                <thead>
                  <tr>
                    <th>Método de Pago</th>
                    <th>Ingreso (S/.)</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Fondo de apertura */}
                  {cuentasData.monto_apertura > 0 && (
                    <tr className="cuentas-fila-apertura">
                      <td className="cuentas-metodo">
                        <span className="cuentas-icono-metodo">🏦</span>
                        Fondo de Apertura
                      </td>
                      <td className="cuentas-monto">
                        S/. {parseFloat(cuentasData.monto_apertura).toFixed(2)}
                      </td>
                    </tr>
                  )}
                  {/* Ventas por método de pago */}
                  {Object.entries(cuentasData.desglose).map(([metodo, total]) => (
                    <tr key={metodo}>
                      <td className="cuentas-metodo">
                        <span className={`cuentas-icono-metodo ${metodo}`}>
                          {metodo === 'efectivo' ? '💵' :
                           metodo === 'yape' ? '📱' :
                           metodo === 'plin' ? '📲' :
                           metodo === 'tarjeta' ? '💳' : '💰'}
                        </span>
                        {metodo.charAt(0).toUpperCase() + metodo.slice(1)}
                      </td>
                      <td className="cuentas-monto">
                        S/. {parseFloat(total).toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="cuentas-total-row">
                    <td><strong>TOTAL EN CAJA</strong></td>
                    <td><strong>S/. {(parseFloat(cuentasData.totalGeneral) + parseFloat(cuentasData.monto_apertura || 0)).toFixed(2)}</strong></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          {!cuentasLoading && !cuentasError && !cuentasData && (
            <div className="sin-pedidos">No hay datos de ingresos para esta caja.</div>
          )}
        </div>
      )}

      {/* Renderizado condicional de los modales */}
      {mostrarModalApertura && <AperturaCajaModal onClose={handleAperturaExitosa} />}
      {mostrarModalCierre && <CierreCajaModal onClose={handleCierreExitoso} cajaACerrar={cajaACerrar} />}
      {mostrarModalAsignacion && <AsignacionCajerosModal onClose={() => setMostrarModalAsignacion(false)} />}

      {/* FAB - Acceso rápido a Pedido Directo */}
      <button
        className="fab-pedido-directo"
        title="Nuevo Pedido Directo"
        onClick={() => navigate('/pedido-directo')}
      >
        ⚡
      </button>
    </div>
  );
};

export default CajaComponent;
